import { z } from 'zod';
/**
 * Open5e SRD Types
 *
 * Types for importing monsters, spells, and other content from the Open5e API.
 * API docs: https://api.open5e.com/v1/
 */
// =============================================================================
// MONSTER (from Open5e API)
// =============================================================================
export const SrdSpeedSchema = z.object({
    walk: z.number().optional(),
    swim: z.number().optional(),
    fly: z.number().optional(),
    burrow: z.number().optional(),
    climb: z.number().optional(),
    hover: z.boolean().optional(),
});
export const SrdActionSchema = z.object({
    name: z.string(),
    desc: z.string(),
    attack_bonus: z.number().optional(),
    damage_dice: z.string().optional(),
    damage_bonus: z.number().optional(),
});
export const SrdSpecialAbilitySchema = z.object({
    name: z.string(),
    desc: z.string(),
});
export const SrdMonsterSchema = z.object({
    // Identity
    slug: z.string(),
    name: z.string(),
    desc: z.string().optional(),
    size: z.string(),
    type: z.string(),
    subtype: z.string().optional(),
    group: z.string().nullable().optional(),
    alignment: z.string(),
    // Combat stats
    armor_class: z.number(),
    armor_desc: z.string().nullable().optional(),
    hit_points: z.number(),
    hit_dice: z.string(),
    // Ability scores
    strength: z.number(),
    dexterity: z.number(),
    constitution: z.number(),
    intelligence: z.number(),
    wisdom: z.number(),
    charisma: z.number(),
    // Saves (nullable)
    strength_save: z.number().nullable().optional(),
    dexterity_save: z.number().nullable().optional(),
    constitution_save: z.number().nullable().optional(),
    intelligence_save: z.number().nullable().optional(),
    wisdom_save: z.number().nullable().optional(),
    charisma_save: z.number().nullable().optional(),
    // Skills & senses
    skills: z.record(z.number()).optional(),
    perception: z.number().nullable().optional(),
    senses: z.string(),
    languages: z.string(),
    // CR
    challenge_rating: z.string(),
    cr: z.number().optional(),
    // Resistances & immunities
    damage_vulnerabilities: z.string(),
    damage_resistances: z.string(),
    damage_immunities: z.string(),
    condition_immunities: z.string(),
    // Movement
    speed: SrdSpeedSchema,
    // Actions & abilities
    actions: z.array(SrdActionSchema).optional(),
    bonus_actions: z.array(SrdActionSchema).nullable().optional(),
    reactions: z.array(SrdActionSchema).nullable().optional(),
    special_abilities: z.array(SrdSpecialAbilitySchema).nullable().optional(),
    legendary_actions: z.array(SrdActionSchema).nullable().optional(),
    legendary_desc: z.string().nullable().optional(),
    // Metadata
    img_main: z.string().nullable().optional(),
    document__slug: z.string().optional(),
    document__title: z.string().optional(),
    environments: z.array(z.string()).optional(),
    spell_list: z.array(z.string()).optional(),
});
// =============================================================================
// SPELL (from Open5e API)
// =============================================================================
export const SrdSpellSchema = z.object({
    slug: z.string(),
    name: z.string(),
    desc: z.string(),
    higher_level: z.string().optional(),
    // Level & school
    level: z.string(),
    level_int: z.number().optional(),
    school: z.string(),
    // Casting
    casting_time: z.string(),
    duration: z.string(),
    range: z.string(),
    // Components
    components: z.string(),
    requires_verbal_components: z.boolean().optional(),
    requires_somatic_components: z.boolean().optional(),
    requires_material_components: z.boolean().optional(),
    material: z.string().optional(),
    // Attributes
    concentration: z.string().optional(),
    requires_concentration: z.boolean().optional(),
    ritual: z.string().optional(),
    can_be_cast_as_ritual: z.boolean().optional(),
    // Classes
    dnd_class: z.string(),
    spell_lists: z.array(z.string()).optional(),
    // Metadata
    document__slug: z.string().optional(),
    document__title: z.string().optional(),
});
// =============================================================================
// API RESPONSE WRAPPERS
// =============================================================================
export const PaginatedResponseSchema = (itemSchema) => z.object({
    count: z.number(),
    next: z.string().nullable(),
    previous: z.string().nullable(),
    results: z.array(itemSchema),
});
import { getHpTier } from './ai-types';
/**
 * Convert SRD monster to Machina Combatant
 */
export function srdMonsterToCombatant(monster, options = {}) {
    const id = options.id || `${monster.slug}-${crypto.randomUUID().slice(0, 8)}`;
    return {
        id,
        name: monster.name,
        type: 'mob',
        currentHp: monster.hit_points,
        maxHp: monster.hit_points,
        hpTier: getHpTier(monster.hit_points, monster.hit_points),
        ac: monster.armor_class,
        position: options.position || { x: 0, y: 0 },
        conditions: [],
        concentratingOn: null,
        availableActions: extractActions(monster),
        recentActions: [],
    };
}
/**
 * Create MobBrain from SRD monster
 */
export function srdMonsterToMobBrain(monster) {
    return {
        mobId: monster.slug,
        mobType: monster.type,
        eligibleForAI: true,
        constraints: {
            fleeThreshold: deriveFleeThreshold(monster),
        },
        memoryScope: 'ENCOUNTER',
        remembers: [],
        baseBehaviors: deriveBehaviors(monster),
        barks: generateBarks(monster),
    };
}
/**
 * Extract available actions from monster data
 */
function extractActions(monster) {
    const actions = ['Attack', 'Dash', 'Dodge', 'Disengage', 'Help', 'Hide'];
    if (monster.actions) {
        for (const action of monster.actions) {
            if (!actions.includes(action.name)) {
                actions.push(action.name);
            }
        }
    }
    if (monster.special_abilities) {
        for (const ability of monster.special_abilities) {
            // Add abilities that look like actions
            if (ability.name.includes('Attack') || ability.desc.includes('action')) {
                if (!actions.includes(ability.name)) {
                    actions.push(ability.name);
                }
            }
        }
    }
    return actions;
}
/**
 * Derive flee threshold from monster stats
 */
function deriveFleeThreshold(monster) {
    // Wisdom affects self-preservation
    if (monster.wisdom >= 14)
        return 0.3;
    if (monster.wisdom <= 6)
        return 0.1;
    // Undead and constructs don't flee
    const type = monster.type.toLowerCase();
    if (type === 'undead' || type === 'construct')
        return 0;
    return 0.2;
}
/**
 * Derive weighted behaviors from monster stats
 * Actions must be from MobAction enum: ATTACK, MULTIATTACK, CAST_SPELL, USE_ABILITY,
 * DASH, DISENGAGE, DODGE, HIDE, HELP, READY, SHOVE, GRAPPLE
 */
function deriveBehaviors(monster) {
    const behaviors = [];
    const alignment = monster.alignment.toLowerCase();
    const type = monster.type.toLowerCase();
    // Check if monster has multiattack
    const hasMultiattack = monster.actions?.some(a => a.name.toLowerCase().includes('multiattack')) ?? false;
    // Check for spellcasting
    const hasSpellcasting = monster.special_abilities?.some(a => a.name.toLowerCase().includes('spellcasting')) ?? false;
    // Base attack weight from alignment
    let attackWeight = 50;
    if (alignment.includes('evil'))
        attackWeight += 20;
    if (alignment.includes('chaotic'))
        attackWeight += 10;
    if (alignment.includes('good'))
        attackWeight -= 20;
    // Primary attack action
    if (hasMultiattack) {
        behaviors.push({
            action: 'MULTIATTACK',
            weight: attackWeight,
        });
    }
    else {
        behaviors.push({
            action: 'ATTACK',
            weight: attackWeight,
        });
    }
    // Spellcasting
    if (hasSpellcasting) {
        behaviors.push({
            action: 'CAST_SPELL',
            weight: 40,
            condition: 'has_spell_slots',
        });
    }
    // Self-preservation behaviors
    let selfPreservation = 30;
    if (type === 'beast')
        selfPreservation += 20;
    if (type === 'undead')
        selfPreservation -= 30;
    if (selfPreservation > 0) {
        behaviors.push({
            action: 'DODGE',
            weight: selfPreservation,
            condition: 'hp_below_25',
        });
        behaviors.push({
            action: 'DASH',
            weight: Math.max(10, selfPreservation - 10),
            condition: 'hp_below_10',
        });
    }
    // Tactical behaviors
    if (type === 'humanoid' || alignment.includes('lawful')) {
        behaviors.push({
            action: 'HELP',
            weight: 20,
            condition: 'ally_adjacent',
        });
    }
    // Grapple for large/strong creatures
    if (monster.size === 'Large' || monster.size === 'Huge' || monster.strength >= 18) {
        behaviors.push({
            action: 'GRAPPLE',
            weight: 15,
            condition: 'adjacent_to_target',
        });
    }
    return behaviors;
}
/**
 * Generate barks from monster type
 */
function generateBarks(monster) {
    const type = monster.type.toLowerCase();
    const intelligence = monster.intelligence;
    // Low intelligence = no speech
    if (intelligence < 6) {
        return {
            aggro: ['*snarls*', '*growls menacingly*'],
            lowHp: ['*backs away, wounded*', '*whimpers*'],
            allyDeath: ['*howls*'],
            flee: ['*flees*'],
        };
    }
    // Type-specific barks
    if (type === 'undead') {
        return {
            aggro: ['Join us...', 'Fresh meat...', 'You will serve...'],
            lowHp: ['The grave... calls...', 'Pain means nothing...'],
            allyDeath: ['Another will rise...'],
            flee: ['I will return...'],
        };
    }
    if (type === 'fiend') {
        return {
            aggro: ['Your soul is mine!', 'Foolish mortal!'],
            lowHp: ['This isn\'t over!', 'You dare?!'],
            allyDeath: ['Weakling deserved it.'],
            flee: ['We will meet again in hell!'],
        };
    }
    // Default humanoid barks
    return {
        aggro: ['Get them!', 'Intruders!', 'Attack!'],
        lowHp: ['I need help!', 'Fall back!'],
        allyDeath: ['No!', 'You bastards!', 'Revenge!'],
        flee: ['Run!', 'Retreat!', 'Live to fight another day!'],
        victory: ['Ha!', 'That\'ll teach them!'],
    };
}
//# sourceMappingURL=srd-types.js.map